import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Ban, CircleCheck, Pencil, Plus } from "lucide-react";

import { getStaff, updateStaffStatus } from "src/api/settings";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import AddStaff from "src/components/modals/AddStaff";
import EditStaffRole from "src/components/modals/EditStaffRole";
import StaffDetails from "src/components/modals/StaffDetails";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { DataTable, SettingsPanel, type Column, type RowsPerPageOption } from "src/components/common";
import { showSuccessToast } from "src/lib/utils";

interface StaffRow {
	uuid: string;
	first_name?: string;
	last_name?: string;
	phone?: string;
	role?: string;
	store_or_warehouse?: string;
}

type StaffType = "active" | "disabled";

const tabTriggerClass =
	"!tw-bg-white !tw-text-text-soft data-[state=active]:!tw-bg-navy data-[state=active]:!tw-text-white tw-rounded-lg tw-border tw-border-border tw-text-[13px]";

const TeamManagement = () => (
	<SettingsPanel
		title="Team Members"
		description="People who can sign in to this business."
		actions={
			<PermissionWrapper permission="add_member">
				<CustomModal
					title="Add New Team Member"
					size="normal"
					trigger={
						<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
							<Plus className="tw-h-4 tw-w-4" />
							Add Member
						</Button>
					}
				>
					<AddStaff />
				</CustomModal>
			</PermissionWrapper>
		}
	>
		<Tabs defaultValue="active">
			<TabsList className="tw-mb-4 !tw-gap-1 !tw-bg-transparent !tw-p-0">
				<TabsTrigger value="active" className={tabTriggerClass}>
					Active
				</TabsTrigger>
				<TabsTrigger value="disabled" className={tabTriggerClass}>
					Disabled
				</TabsTrigger>
			</TabsList>
			<TabsContent value="active">
				<StaffTable type="active" />
			</TabsContent>
			<TabsContent value="disabled">
				<StaffTable type="disabled" />
			</TabsContent>
		</Tabs>
	</SettingsPanel>
);

const StaffTable = ({ type }: { type: StaffType }) => {
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const canEnable = useHasPermissions("enable_user_account");
	const canDisable = useHasPermissions("disable_user_account");
	const canAssignRole = useHasPermissions("assign_user_role");

	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	// `getStaff`/`updateStaffStatus` are curried by staff type in the API layer.
	const fetchStaff = getStaff(type);
	const setStatus = updateStaffStatus(type);

	const { data, isLoading } = useQuery({
		queryKey: ["teams", type, page, limit],
		queryFn: () => fetchStaff({ page, limit: limit === "All" ? 9999 : limit }),
	});

	const { mutate } = useMutation(setStatus, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Team member updated");
			queryClient.invalidateQueries(["teams"]);
		},
	});

	const staff = data?.data ?? [];

	const columns: Column<StaffRow>[] = [
		{
			key: "name",
			header: "Name",
			primary: true,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "phone", header: "Phone", cell: (row) => row.phone ?? "—" },
		{ key: "role", header: "Role", cell: (row) => <span className="tw-capitalize">{row.role ?? "—"}</span> },
		{ key: "store", header: "Store", cell: (row) => <span className="tw-capitalize">{row.store_or_warehouse ?? "—"}</span> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[140px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1">
					<CustomModal
						title="Staff Details"
						size="normal"
						trigger={
							<button
								type="button"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
							>
								Details
							</button>
						}
					>
						<StaffDetails staff={row} />
					</CustomModal>

					{canAssignRole && type === "active" && (
						<CustomModal
							title="Edit Staff Details"
							trigger={
								<button
									type="button"
									aria-label="Edit staff"
									className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
								>
									<Pencil className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
						>
							<EditStaffRole staff={row} />
						</CustomModal>
					)}

					{canDisable && type === "active" && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									aria-label="Disable member"
									className="tw-rounded-lg tw-p-1.5 tw-text-red tw-transition-colors hover:tw-bg-red-tint"
								>
									<Ban className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
							mutateFn={() => mutate({ uuid: row.uuid })}
						/>
					)}

					{canEnable && type !== "active" && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									aria-label="Enable member"
									className="tw-rounded-lg tw-p-1.5 tw-text-teal tw-transition-colors hover:tw-bg-teal-tint"
								>
									<CircleCheck className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
							mutateFn={() => mutate({ uuid: row.uuid })}
						/>
					)}
				</div>
			),
		},
	];

	return (
		<DataTable
			data={staff}
			columns={columns}
			rowKey={(row) => row.uuid}
			loading={isLoading}
			totalRows={data?.total ?? staff.length}
			page={page}
			onPageChange={(next) => {
				const params = new URLSearchParams(searchParams);
				params.set("page", String(next));
				setSearchParams(params);
			}}
			rowsPerPage={limit}
			onRowsPerPageChange={(next) => {
				const params = new URLSearchParams(searchParams);
				params.set("limit", String(next));
				params.set("page", "1");
				setSearchParams(params);
			}}
			emptyTitle={type === "active" ? "No active team members" : "No disabled team members"}
			emptyDescription={type === "active" ? "Invite a colleague to help run this business." : undefined}
		/>
	);
};

export default TeamManagement;
